/**
 * Fait : les demandes que le client adresse à la bibliothèque de consignes —
 *   création et modification. Toutes deux portent le titre, la description et
 *   le texte ; la modification porte en plus la version qui arbitre les
 *   écritures concurrentes.
 *
 *   LA PORTÉE A DISPARU DES DEUX DEMANDES (lot 6, migration 012). La
 *   bibliothèque est commune depuis `D-025` : demander une portée à quelqu'un
 *   alors que rien n'en tient compte, c'est lui faire croire à un rangement
 *   privé qui n'existe pas. Le champ est retiré, il n'est pas ignoré — un
 *   schéma `strict` refuse une demande qui le porterait encore, et c'est
 *   voulu : un client resté en arrière doit recevoir un refus, pas un silence.
 *
 * Ne fait pas : il ne décrit pas le document STOCKÉ (c'est
 *   `donnees/documents.ts`) et ne décide d'aucun droit — écrire n'appartient
 *   qu'à l'auteur, et c'est le service qui en juge, écrit une fois.
 */

import { z } from 'zod';

import { nomDonne } from './schemas.ts';

/**
 * Les mêmes bornes que le modèle de données. Elles sont répétées ici plutôt
 * qu'importées du point d'entrée serveur : ce module est chargé par le
 * NAVIGATEUR, et `@aymoai/shared/donnees` ne doit jamais l'être.
 *
 * Elles sont répétées, donc elles peuvent diverger — c'est exactement ce que
 * le critère 145 attrape, en exigeant qu'un texte de vingt mille caractères
 * passe ET qu'un caractère de plus soit refusé. Relever une seule des deux
 * bornes fait crier l'un des deux cas.
 */
export const TAILLE_MAXIMALE_TEXTE_DE_PROMPT = 20_000;
export const TAILLE_MAXIMALE_DESCRIPTION_DE_PROMPT = 300;

/**
 * La description est facultative à l'envoi, jamais absente du document : le
 * service pose une chaîne vide quand personne n'en donne. Une consigne peut
 * n'avoir aucune description — c'est le cas de toutes celles d'avant ce lot.
 */
const descriptionDePrompt = z.string().max(TAILLE_MAXIMALE_DESCRIPTION_DE_PROMPT).optional();

export const demandeDePrompt = z
  .object({
    titre: nomDonne,
    description: descriptionDePrompt,
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_DE_PROMPT),
  })
  .strict();

export type DemandeDePrompt = z.infer<typeof demandeDePrompt>;

/** La modification : le titre, la description, le texte, et la version qui arbitre. */
export const demandeDeModificationDePrompt = z
  .object({
    titre: nomDonne,
    description: descriptionDePrompt,
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_DE_PROMPT),
    version: z.number().int().nonnegative(),
  })
  .strict();

export type DemandeDeModificationDePrompt = z.infer<typeof demandeDeModificationDePrompt>;
